/*
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import appTheme from '../../theme/appTheme.json';

interface FeedbackConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackConfirmationDialog: React.FC<FeedbackConfirmationDialogProps> = ({
  open,
  onClose,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: appTheme.primaryBackgroundColor,
          borderRadius: '12px',
          width: 640,
          maxWidth: 640,
          overflow: 'hidden'
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: 3,
          gap: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: appTheme.viasatFontFamilyHeavy,
            fontWeight: 800,
            fontSize: '20px',
            lineHeight: '28px',
            letterSpacing: '0.2px',
            color: appTheme.primaryTextColor,
          }}
        >
          Thank you for your feedback
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: appTheme.primaryTextColor, p: 0, mt: '2px' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
        <Typography
          sx={{
            fontFamily: appTheme.appFontFamily,
            fontSize: appTheme.defaultFontSize,
            lineHeight: '24px',
            color: appTheme.primaryTextColor,
          }}
        >
          Members of our team will review your message and respond promptly.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: `1px solid ${appTheme.mptRowBorderBottom}` }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            height: 40,
            px: 3,
            background: appTheme.mptLightBlue,
            color: appTheme.darkGrayTextColor,
            fontFamily: appTheme.appFontFamily,
            fontWeight: 600,
            fontSize: appTheme.defaultFontSize,
            boxShadow: 'none',
            '&:hover': {
              background: appTheme.mptLightBlue,
              boxShadow: 'none',
              opacity: 0.9,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackConfirmationDialog;
